'use client'
import { useMemo, useState } from 'react'
import type { CountryCode, Option, PaymentProviderConfig } from 'ecosystem'
import { useBuyerVat } from '../../common/hooks'

export interface CheckoutCountries {
  /** Countries the merchant offers. Empty means no restriction. */
  availableCountries: CountryCode[]
  /** The country the provider is configured with. */
  configuredCountry?: CountryCode
}

/**
 * Countries the active payment provider can create a session for.
 *
 * Klarna, Svea and Qliro all create a session per country. Nexi takes the
 * country from its own configuration and gives the shopper no say.
 */
export const resolveCheckoutCountries = (
  paymentProviderConfig?: Option<PaymentProviderConfig>
): CheckoutCountries => {
  const { providerName, providerOptions } = paymentProviderConfig ?? {}

  switch (providerName) {
    case 'KLARNA':
      return {
        availableCountries: providerOptions?.klarna?.availableCountries ?? [],
        configuredCountry: providerOptions?.klarna?.purchaseCountry as CountryCode | undefined
      }
    case 'QLIRO':
      return {
        availableCountries: providerOptions?.qliro?.availableCountries ?? [],
        configuredCountry: providerOptions?.qliro?.countryCode
      }
    case 'SVEA':
      return {
        availableCountries: providerOptions?.svea?.availableCountries ?? [],
        configuredCountry: providerOptions?.svea?.countryCode as CountryCode | undefined
      }
    case 'BRIQPAY':
      return {
        availableCountries: providerOptions?.briqpay?.availableCountries ?? [],
        configuredCountry: providerOptions?.briqpay?.countryCode
      }
    default:
      return { availableCountries: [] }
  }
}

/**
 * The country the payment session is created for.
 *
 * With two or more available countries the shopper picks; until they do, a
 * logged-in company is preselected on its own country — resolved from the
 * billing address, then the legal address, then the company's country (see
 * `resolveBuyerCountry`), which covers a B2B buyer in nearly every case. A
 * company with none of those, and every consumer, falls back to the merchant's
 * configured country.
 *
 * With one or no available country there is nothing to pick, and the value
 * stays on the configured country — the same one the create-order handler will
 * resolve, so the FE never prices delivery against a country the session was
 * not created for.
 */
export const useCheckoutCountry = (paymentProviderConfig?: Option<PaymentProviderConfig>) => {
  const { availableCountries, configuredCountry } = useMemo(
    () => resolveCheckoutCountries(paymentProviderConfig),
    [paymentProviderConfig]
  )
  const { buyerCountry } = useBuyerVat(paymentProviderConfig)

  const hasCountryChoice = availableCountries.length > 1

  const preferredCountry = useMemo(() => {
    if (!hasCountryChoice) {
      return configuredCountry
    }

    const isOffered = (country?: string): country is CountryCode =>
      !!country && availableCountries.includes(country as CountryCode)

    // The buyer's own country wins, but only if the merchant actually ships
    // the session there.
    if (isOffered(buyerCountry)) {
      return buyerCountry
    }

    return isOffered(configuredCountry) ? configuredCountry : availableCountries[0]
  }, [availableCountries, buyerCountry, configuredCountry, hasCountryChoice])

  // The shopper's explicit pick, kept separate so a late-arriving customer
  // record cannot overwrite a deliberate choice.
  const [pickedCountry, setPickedCountry] = useState<CountryCode | undefined>(undefined)

  return {
    availableCountries,
    configuredCountry,
    hasCountryChoice,
    selectedCountryCode: pickedCountry ?? preferredCountry,
    setSelectedCountryCode: setPickedCountry
  }
}
